/**
 * Holidays API
 * Handles holiday management calls
 */
import apiClient from './apiClient';

export interface Holiday {
  id: number;
  name: string;
  date: string;
  day_type: 'full' | 'half';
  is_optional: boolean;
  year: number;
  created_by: number | null;
  created_at: string;
}

export interface HolidaysListResponse {
  holidays: Holiday[];
  total: number;
}

export interface CreateHolidayData {
  name: string;
  date: string;
  day_type?: 'full' | 'half';
  is_optional?: boolean;
}

export interface UpdateHolidayData {
  name?: string;
  date?: string;
  day_type?: 'full' | 'half';
  is_optional?: boolean;
}

export interface CheckHolidayResponse {
  date: string;
  is_holiday: boolean;
  holiday: Holiday | null;
}

export const holidaysApi = {
  /**
   * Get all holidays
   */
  getHolidays: async (params?: { year?: number; include_optional?: boolean }): Promise<HolidaysListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.year) searchParams.set('year', params.year.toString());
    if (params?.include_optional !== undefined) searchParams.set('include_optional', params.include_optional.toString());
    
    const queryString = searchParams.toString();
    return apiClient.get<HolidaysListResponse>(`/holidays${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a holiday by ID
   */
  getHoliday: async (holidayId: number): Promise<{ holiday: Holiday }> => {
    return apiClient.get<{ holiday: Holiday }>(`/holidays/${holidayId}`);
  },

  /**
   * Create a new holiday
   */
  createHoliday: async (data: CreateHolidayData): Promise<{ message: string; holiday: Holiday }> => {
    return apiClient.post<{ message: string; holiday: Holiday }>('/holidays', data);
  },

  /**
   * Update a holiday
   */
  updateHoliday: async (holidayId: number, data: UpdateHolidayData): Promise<{ message: string; holiday: Holiday }> => {
    return apiClient.put<{ message: string; holiday: Holiday }>(`/holidays/${holidayId}`, data);
  },

  /**
   * Delete a holiday
   */
  deleteHoliday: async (holidayId: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/holidays/${holidayId}`);
  },

  /**
   * Check if a specific date is a holiday
   */
  checkHoliday: async (date: string): Promise<CheckHolidayResponse> => {
    return apiClient.get<CheckHolidayResponse>(`/holidays/check/${date}`);
  },
};

export default holidaysApi;
